import { useCallback, useState } from 'react'

/** Per-viewer preferences (ADR 0012, 0017, 0019): `expanded` | `collapsed`. */
export const RAIL_KEY = 'oase-admin:rail'
export const SIDEBAR_KEY = 'oase-admin:sidebar'

function read(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(key)
    return value === null ? fallback : value === 'expanded'
  } catch {
    return fallback
  }
}

function usePreference(key: string, fallback: boolean): [boolean, (expanded: boolean) => void] {
  const [isExpanded, setExpandedState] = useState(() => read(key, fallback))
  const setExpanded = useCallback(
    (expanded: boolean) => {
      setExpandedState(expanded)
      try {
        localStorage.setItem(key, expanded ? 'expanded' : 'collapsed')
      } catch {
        // storage unavailable: the preference lasts for this page only
      }
    },
    [key]
  )
  return [isExpanded, setExpanded]
}

/** Whether the tablet icon rail shows labels. Collapsed by default; persisted. */
export function useRailExpanded(): [boolean, (expanded: boolean) => void] {
  return usePreference(RAIL_KEY, false)
}

/**
 * Whether the docked desktop sidebar shows labels. Expanded by default, as
 * react-admin's docked sidebar (180px open, 56px closed); persisted apart
 * from the rail, so collapsing on desktop doesn't change the tablet default.
 */
export function useSidebarExpanded(): [boolean, (expanded: boolean) => void] {
  return usePreference(SIDEBAR_KEY, true)
}
